import {
  AlarmTriggerState,
  NotificationEvent,
  SortOrder,
  useGetNotificationsQuery,
} from '../../gql/generated';
import { formatMetricName } from '../../utils/formatMetric';
import {
  formatNotificationDate,
  notificationStateLabel,
  notificationStateVariant,
} from '../../utils/notificationFormatters';
import { Badge } from '../Badge';

interface MachineEventLogTabProps {
  machineId: string;
}

const EVENT_LIMIT = 50;

/** What an event is called: the alert it belongs to, or the subject EDMS sent. */
function eventTitle(event: NotificationEvent): string {
  return event.alarmName || event.subject || 'Pulse alert';
}

/**
 * MachineEventLogTab - The Pulse alerts that fired on one machine, from any
 * user, newest first. Each event names its alert and the signal it watches.
 */
export function MachineEventLogTab({ machineId }: MachineEventLogTabProps) {
  const { data, loading, error } = useGetNotificationsQuery({
    variables: {
      filter: { machineIds: [machineId], allUsers: true },
      orderBy: { timeUtc: SortOrder.desc },
      pagination: { limit: EVENT_LIMIT, offset: 0 },
    },
    fetchPolicy: 'cache-and-network',
  });

  const events = data?.notifications.rows ?? [];

  if (loading && !data) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-tertiary'>Loading Pulse alerts...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-error'>Error loading Pulse alerts</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-12'>
        <p className='size-p2 text-tertiary'>No Pulse alerts recorded</p>
      </div>
    );
  }

  return (
    <ul className='overflow-hidden rounded-xl border border-border-secondary bg-white'>
      {events.map((event, index) => {
        const state: AlarmTriggerState = event.alarmTriggerState;

        return (
          <li
            key={event.id}
            className={index > 0 ? 'border-border-secondary/70 border-t' : ''}
          >
            <article className='px-6 py-4'>
              <div className='flex flex-wrap items-center gap-2'>
                <h3 className='size-p1 text-primary font-medium'>
                  {eventTitle(event)}
                </h3>
                {event.signal && (
                  <Badge variant='neutral' showDot={false}>
                    {formatMetricName(event.signal)}
                  </Badge>
                )}
              </div>

              <div className='mt-2 flex flex-wrap items-center gap-2'>
                <Badge variant={notificationStateVariant[state]}>
                  {notificationStateLabel[state]}
                </Badge>
                <span className='size-caption text-tertiary'>
                  {formatNotificationDate(event.timeUtc)}
                </span>
              </div>
            </article>
          </li>
        );
      })}
    </ul>
  );
}
